import React, { memo, useCallback, useRef, useState } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { FormikHelpers, FormikState } from 'formik';
import cn from 'classnames';

import { makeStyles } from '~/UIKit/Style';
import { Button, Typography } from '~/UIKit';
import {
  CHARCOAL_GREY,
  FADED_ICON_ERROR,
  GREY,
  LIGHT_BLUE,
  PLACEHOLDER,
} from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGISH } from '~/Constants/UI.constants';
import { IModalProps } from '~/UIKit/Form/ModalSelect/popovers/types';

const useStyles = makeStyles(
  () => ({
    container: {
      cursor: 'pointer',
      overflowWrap: 'anywhere',
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      borderRadius: 30,
      minWidth: 30,
      maxWidth: 30,
      minHeight: 30,
      maxHeight: 30,
      lineHeight: '15px',
      fontSize: FONT_SIZE_LARGISH,
      display: 'flex',
      alignItems: 'center',
      placeContent: 'center',
      marginRight: 15,
      color: CHARCOAL_GREY,
      backgroundColor: LIGHT_BLUE,
      '& > svg': {
        fontSize: 'inherit',
      },
    },
    label: {
      maxWidth: '100%',
      marginRight: 15,
      lineHeight: 1,
      overflowWrap: 'break-word',
    },
    button: {
      color: GREY,
      fontWeight: 'normal',
      marginLeft: 'auto',
      paddingRight: 0,
      // Offsets Chevron padding
      marginRight: '-8px',
      lineHeight: 1,
      display: 'grid',
      gridTemplateColumns: '1fr 25px',
    },
    selected: {
      color: CHARCOAL_GREY,
    },
    ChevronRight: {
      color: GREY,
      display: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit',
    },
    error: {
      backgroundColor: FADED_ICON_ERROR,
    },
    placeholder: {
      color: PLACEHOLDER,
    },
  }),
  { name: 'ModalSelect' },
);

export interface IModalSelect extends FormikState<any> {
  name: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  Modal: React.FC<IModalProps<any>>;
  value?: string | number | (string | number)[];
  disabled?: boolean;
  className?: string;
  formatValue: (value: any) => React.ReactNode | void | null;
  placeholder: string;
  setFieldValue: FormikHelpers<any>['setFieldValue'];
}

export const ModalSelect = memo(
  ({
    name,
    icon,
    label,
    Modal,
    value,
    errors,
    disabled,
    className,
    formatValue,
    placeholder,
    setFieldValue,
  }: IModalSelect) => {
    const { classes } = useStyles();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const content = formatValue(value) || (
      <div className={classes.placeholder}>{placeholder ?? ''}</div>
    );

    const onClose = useCallback(() => setOpen(false), [setOpen]);

    return (
      <label className={cn(className, classes.container, { [classes.error]: !!errors })}>
        <div className={classes.icon}>{icon}</div>
        <Typography className={classes.label}>{label}</Typography>
        <Button
          ref={buttonRef}
          type="button"
          className={cn(classes.button, { [classes.selected]: content })}
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          {content}
          <span className={cn(classes.ChevronRight, { [classes.placeholder]: !content })}>
            <ChevronRight />
          </span>
        </Button>
        <Modal
          open={open}
          name={name}
          onClose={onClose}
          anchorEl={buttonRef.current}
          selected={value}
          setFieldValue={setFieldValue}
        />
      </label>
    );
  },
);
